/**
 * Date key helpers.
 *
 * IMPORTANT: Use local calendar day (YYYY-MM-DD) for UI grouping.
 * Avoid `toISOString().split('T')[0]` for day bucketing because it uses UTC and can
 * shift items to the previous/next day depending on the user's timezone.
 */

export const toLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const fromLocalDateKey = (dateKey: string): Date => {
  const [y, m, d] = dateKey.split('-').map((v) => parseInt(v, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return new Date(NaN);
  }
  return new Date(y, m - 1, d);
};

/**
 * Safely coerce an unknown value into a valid Date.
 * Returns null when the value cannot be converted.
 */
export const coerceDate = (value: unknown): Date | null => {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }
  if (typeof value === 'string') {
    const dateOnlyMatch = /^(\d{4}-\d{2}-\d{2})$/.exec(value);
    const isoMidnightUtcMatch = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.000)?Z$/.exec(value);
    const parsed = dateOnlyMatch
      ? fromLocalDateKey(dateOnlyMatch[1])
      : isoMidnightUtcMatch
        ? fromLocalDateKey(isoMidnightUtcMatch[1])
        : new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  if (typeof value === 'number') {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }
  if (value && typeof value === 'object') {
    const maybeToDate = (value as { toDate?: () => Date }).toDate;
    if (typeof maybeToDate === 'function') {
      const parsed = maybeToDate.call(value);
      return parsed instanceof Date && Number.isFinite(parsed.getTime()) ? parsed : null;
    }
    const seconds = (value as { seconds?: number }).seconds;
    if (typeof seconds === 'number' && Number.isFinite(seconds)) {
      const nanos = (value as { nanoseconds?: number }).nanoseconds;
      const ms = seconds * 1000 + (typeof nanos === 'number' ? Math.floor(nanos / 1e6) : 0);
      const parsed = new Date(ms);
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    }
  }
  return null;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Inclusive calendar-day count between two dates.
 *
 * Examples:
 * - Dec 16 → Dec 16 = 1 day
 * - Dec 16 → Dec 18 = 3 days
 *
 * Dates are normalized to local midnight to avoid timezone/hour skew.
 */
export const getInclusiveDayCount = (startDate: Date, endDate: Date): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;

  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffDays = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
  return diffDays >= 0 ? diffDays + 1 : 0;
};


